import {
  CalendarClock,
  CircleAlert,
  Hash,
  LoaderCircle,
  Save,
  X,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, ApiError } from "../api";
import type { AutoDormCheckPlan } from "../types";
import { Tag } from "./design-system";

export function AutoDormCheckPlanEditor({
  plan,
  onClose,
  onSaved,
  onStale,
}: {
  plan: AutoDormCheckPlan;
  onClose: () => void;
  onSaved: (plan: AutoDormCheckPlan) => void;
  onStale: () => Promise<unknown> | unknown;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const priceInputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const [price, setPrice] = useState(() => formatPriceInput(plan.priceCents));
  const [quota, setQuota] = useState(() => String(plan.quotaAmount));
  const [description, setDescription] = useState(plan.description);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    window.setTimeout(() => priceInputRef.current?.select(), 0);
    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  const validation = useMemo(
    () => validatePlanDraft(plan, { price, quota, description }),
    [description, plan, price, quota],
  );
  const changed =
    validation.value !== null &&
    (validation.value.priceCents !== plan.priceCents ||
      validation.value.quotaAmount !== plan.quotaAmount ||
      validation.value.description !== plan.description);

  const submit = async () => {
    if (!validation.value || !changed || saving) return;
    setSaving(true);
    try {
      const saved = await api.updateAutoDormCheckPlan(plan.id, {
        ...validation.value,
        version: plan.version,
      });
      onSaved(saved);
      toast.success("套餐已更新");
      onClose();
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.code === "AUTO_DORM_CHECK_PLAN_STALE"
      ) {
        await onStale();
      }
      toast.error(error instanceof Error ? error.message : "套餐保存失败");
    } finally {
      setSaving(false);
    }
  };

  const Icon = plan.billingType === "time" ? CalendarClock : Hash;
  const quotaUnitLabel =
    plan.quotaUnit === "day"
      ? "天"
      : plan.quotaUnit === "semester"
        ? "学期"
        : "次";
  const quotaHint =
    plan.quotaUnit === "day"
      ? "1–3650 天"
      : plan.quotaUnit === "semester"
        ? "1–8 学期"
        : "1–100000 次";

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      className="m-auto max-h-[calc(100vh-24px)] w-[min(560px,calc(100vw-24px))] overflow-hidden rounded-2xl border border-sand-light bg-paper p-0 text-ink shadow-2xl backdrop:bg-ink/35 backdrop:backdrop-blur-sm"
      onCancel={(event) => {
        event.preventDefault();
        if (!saving) onClose();
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current && !saving) onClose();
      }}
    >
      <form
        className="flex max-h-[calc(100vh-24px)] flex-col"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <header className="flex flex-none items-start gap-4 border-b border-sand-light/70 px-5 py-5 sm:px-6">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-clay-soft text-clay-deep">
            <Icon size={19} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id={titleId} className="font-display text-lg font-medium">
                编辑{plan.name}套餐
              </h2>
              <Tag tone={plan.billingType === "time" ? "clay" : "sage"}>
                {plan.billingType === "time" ? "按时间" : "按次数"}
              </Tag>
            </div>
            <p
              id={descriptionId}
              className="mt-1 text-[12px] leading-relaxed text-ink-soft"
            >
              新配置只用于之后创建的订单，历史订单与已到账额度保持不变。
            </p>
          </div>
          <button
            type="button"
            aria-label="关闭"
            disabled={saving}
            onClick={onClose}
            className="rounded-lg p-2 text-ink-faint transition hover:bg-peach hover:text-ink disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </header>

        <fieldset
          disabled={saving}
          className="scrollbar-thin min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 disabled:opacity-65 sm:px-6"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block min-w-0">
              <span className="text-[11.5px] font-medium text-ink">价格</span>
              <span className="mt-2 flex h-11 items-center rounded-xl border border-sand-light bg-cream/35 px-3.5 transition focus-within:border-clay focus-within:bg-paper">
                <span className="mr-2 font-mono text-[13px] text-ink-faint">
                  ¥
                </span>
                <input
                  ref={priceInputRef}
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                  aria-invalid={Boolean(price && !parsePriceCents(price))}
                  className="min-w-0 flex-1 bg-transparent font-mono text-[15px] font-medium text-ink outline-none"
                />
                <span className="ml-2 text-[10.5px] text-ink-faint">元</span>
              </span>
              <small className="mt-1.5 block text-[10.5px] text-ink-faint">
                0.01–10000.00 元，最多两位小数
              </small>
            </label>

            <label className="block min-w-0">
              <span className="text-[11.5px] font-medium text-ink">额度</span>
              <span className="mt-2 flex h-11 items-center rounded-xl border border-sand-light bg-cream/35 px-3.5 transition focus-within:border-clay focus-within:bg-paper">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={quota}
                  onChange={(event) => setQuota(event.target.value)}
                  className="min-w-0 flex-1 bg-transparent font-mono text-[15px] font-medium text-ink outline-none"
                />
                <span className="ml-2 text-[10.5px] text-ink-faint">
                  {quotaUnitLabel}
                </span>
              </span>
              <small className="mt-1.5 block text-[10.5px] text-ink-faint">
                {quotaHint}，必须为整数
              </small>
            </label>
          </div>

          <label className="block">
            <span className="flex items-center justify-between gap-3 text-[11.5px] font-medium text-ink">
              套餐说明
              <small className="font-normal text-ink-faint">
                {description.length}/240
              </small>
            </span>
            <textarea
              value={description}
              maxLength={240}
              rows={4}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="可留空"
              className="mt-2 w-full resize-none rounded-xl border border-sand-light bg-cream/35 px-3.5 py-3 text-[12.5px] leading-relaxed text-ink outline-none transition placeholder:text-ink-faint/65 focus:border-clay focus:bg-paper"
            />
          </label>

          {validation.error ? (
            <p className="flex items-center gap-2 rounded-xl border border-destructive/20 bg-destructive/5 px-3.5 py-2.5 text-[11.5px] text-destructive">
              <CircleAlert size={14} />
              {validation.error}
            </p>
          ) : null}
        </fieldset>

        <footer className="flex flex-none items-center justify-end gap-2 border-t border-sand-light/70 bg-cream/35 px-5 py-4 sm:px-6">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="adc-button adc-button-outline"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={!validation.value || !changed || saving}
            className="adc-button adc-button-dark min-w-24"
          >
            {saving ? (
              <LoaderCircle size={14} className="animate-spin" />
            ) : (
              <Save size={14} />
            )}
            {saving ? "保存中" : changed ? "保存" : "未修改"}
          </button>
        </footer>
      </form>
    </dialog>
  );
}

function validatePlanDraft(
  plan: AutoDormCheckPlan,
  draft: { price: string; quota: string; description: string },
): {
  value: {
    priceCents: number;
    quotaAmount: number;
    description: string;
  } | null;
  error: string | null;
} {
  const priceCents = parsePriceCents(draft.price);
  if (priceCents === null) {
    return { value: null, error: "请输入 0.01 到 10000.00 元的有效价格。" };
  }
  if (!/^\d+$/.test(draft.quota.trim())) {
    return { value: null, error: "额度必须填写为整数。" };
  }
  const quotaAmount = Number(draft.quota);
  const maximum =
    plan.quotaUnit === "day"
      ? 3650
      : plan.quotaUnit === "semester"
        ? 8
        : 100_000;
  if (
    !Number.isSafeInteger(quotaAmount) ||
    quotaAmount < 1 ||
    quotaAmount > maximum
  ) {
    return {
      value: null,
      error: `额度必须在 1 到 ${maximum} 之间。`,
    };
  }
  const description = draft.description.trim();
  if (description.length > 240) {
    return { value: null, error: "套餐说明不能超过 240 个字符。" };
  }
  return {
    value: { priceCents, quotaAmount, description },
    error: null,
  };
}

function parsePriceCents(value: string): number | null {
  const normalized = value.trim();
  if (!/^(?:0|[1-9]\d{0,4})(?:\.\d{1,2})?$/.test(normalized)) return null;
  const [yuan, fraction = ""] = normalized.split(".");
  const cents = Number(yuan) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(cents) && cents >= 1 && cents <= 1_000_000
    ? cents
    : null;
}

function formatPriceInput(priceCents: number): string {
  return (priceCents / 100).toFixed(2);
}
